import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const TIER_STEP = 50;
const MAX_CEILING = 5000;
const DISMISS_KEY = 'kt-protection-dismissed-token';

/** [cart value ceiling in dollars, variant id] for every $50 protection tier */
const TIERS = [[50,46033564008482],[100,46033564041250],[150,46033564074018],[200,46033564106786],[250,46033564139554],[300,46033564172322],[350,46033564205090],[400,46033564237858],[450,46033564270626],[500,46033564303394],[550,46033564336162],[600,46033564368930],[650,46033564401698],[700,46033564434466],[750,46033564467234],[800,46033564500002],[850,46033564532770],[900,46033564565538],[950,46033564598306],[1000,46033564631074],[1050,46033564663842],[1100,46033564696610],[1150,46033564729378],[1200,46033564762146],[1250,46033564794914],[1300,46033564827682],[1350,46033564860450],[1400,46033564893218],[1450,46033564925986],[1500,46033564958754],[1550,46033564991522],[1600,46033565024290],[1650,46033565057058],[1700,46033565089826],[1750,46033565122594],[1800,46033565155362],[1850,46033565188130],[1900,46033565220898],[1950,46033565253666],[2000,46033565286434],[2050,46033565319202],[2100,46033565351970],[2150,46033565384738],[2200,46033565417506],[2250,46033565450274],[2300,46033565483042],[2350,46033565515810],[2400,46033565548578],[2450,46033565581346],[2500,46033565614114],[2550,46033565646882],[2600,46033565679650],[2650,46033565712418],[2700,46033565745186],[2750,46033565777954],[2800,46033565810722],[2850,46033565843490],[2900,46033565876258],[2950,46033565909026],[3000,46033565941794],[3050,46033565974562],[3100,46033566007330],[3150,46033566040098],[3200,46033566072866],[3250,46033566105634],[3300,46033566138402],[3350,46033566171170],[3400,46033566203938],[3450,46033566236706],[3500,46033566269474],[3550,46033566302242],[3600,46033566335010],[3650,46033566367778],[3700,46033566400546],[3750,46033566433314],[3800,46033566466082],[3850,46033566498850],[3900,46033566531618],[3950,46033566564386],[4000,46033566597154],[4050,46033566629922],[4100,46033566662690],[4150,46033566695458],[4200,46033566728226],[4250,46033566760994],[4300,46033566793762],[4350,46033566826530],[4400,46033566859298],[4450,46033566892066],[4500,46033566924834],[4550,46033566957602],[4600,46033566990370],[4650,46033567023138],[4700,46033567055906],[4750,46033567088674],[4800,46033567121442],[4850,46033567154210],[4900,46033567186978],[4950,46033567219746],[5000,46033567252514]];

const PROTECTION_VARIANT_IDS = new Set(TIERS.map(([, id]) => id));

/** Sources that indicate a real person removed/changed a cart line via the cart UI */
const USER_LINE_ACTION_SOURCES = new Set(['cart-items-component', 'quick-order-remove-all', 'quick-order-quantity']);

let syncing = false;

function tierForDollars(dollars) {
  if (dollars <= 0) return null;
  const ceiling = Math.min(Math.ceil(dollars / TIER_STEP) * TIER_STEP, MAX_CEILING);
  const match = TIERS.find(([tierCeiling]) => tierCeiling === ceiling);
  return match ? { ceiling: match[0], id: match[1] } : { ceiling: MAX_CEILING, id: TIERS[TIERS.length - 1][1] };
}

function protectionLinesIn(cart) {
  return cart.items.filter((item) => PROTECTION_VARIANT_IDS.has(item.variant_id));
}

function otherSubtotalCents(cart) {
  return cart.items.reduce((sum, item) => (PROTECTION_VARIANT_IDS.has(item.variant_id) ? sum : sum + item.line_price), 0);
}

function isDismissed(token) {
  return sessionStorage.getItem(DISMISS_KEY) === token;
}

function setDismissed(token) {
  sessionStorage.setItem(DISMISS_KEY, token);
}

function clearDismissed() {
  sessionStorage.removeItem(DISMISS_KEY);
}

function getCart() {
  return fetch('/cart.js').then((r) => r.json());
}

function addVariant(variantId) {
  return fetch(Theme.routes.cart_add_url, fetchConfig('json', {
    body: JSON.stringify({ items: [{ id: String(variantId), quantity: 1 }] }),
  }))
    .then((r) => r.json())
    .then((data) => {
      if (data.errors) console.error('[ka-package-protection] add failed', data.errors);
      return data;
    });
}

function changeVariant(variantId, quantity) {
  return fetch(Theme.routes.cart_change_url, fetchConfig('json', {
    body: JSON.stringify({ id: String(variantId), quantity }),
  }))
    .then((r) => r.json())
    .then((data) => {
      if (data.errors) console.error('[ka-package-protection] change failed', data.errors);
      return data;
    });
}

function announceCartUpdate(cart) {
  const event = new Event(ThemeEvents.cartUpdate, { bubbles: true });
  event.detail = {
    resource: cart,
    sourceId: 'ka-package-protection',
    data: { source: 'ka-package-protection', itemCount: cart.item_count },
  };
  document.dispatchEvent(event);
}

/**
 * Package protection is opt-in only (spec D38/D39: no preselected paid add-on, no new fees).
 * This used to ADD the matching tier to every cart on load and on every cart update, which put a
 * second line in the cart after one Add to cart tap (R28 T16, #110). Now it only keeps a line the
 * shopper already has in step with the cart value, or drops it when the cart is otherwise empty.
 * It never adds one on its own.
 */
async function syncProtection(cart) {
  if (syncing) return;
  syncing = true;

  try {
    if (!cart) cart = await getCart();

    const others = otherSubtotalCents(cart);
    const existing = protectionLinesIn(cart);

    if (!existing.length) return;

    if (others === 0) {
      for (const line of existing) await changeVariant(line.variant_id, 0);
      announceCartUpdate(await getCart());
      clearDismissed();
      return;
    }

    if (isDismissed(cart.token)) return;

    const desired = tierForDollars(others / 100);
    if (existing.length === 1 && existing[0].variant_id === desired.id) return;

    for (const line of existing) await changeVariant(line.variant_id, 0);
    await addVariant(desired.id);
    announceCartUpdate(await getCart());
  } finally {
    syncing = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (syncing) return;

  const resource = event.detail?.resource;
  const source = event.detail?.data?.source;
  const cart = resource && Array.isArray(resource.items) ? resource : null;

  if (cart && USER_LINE_ACTION_SOURCES.has(source)) {
    const stillHasProtection = protectionLinesIn(cart).length > 0;
    if (!stillHasProtection) {
      setDismissed(cart.token);
      return;
    }
  }

  syncProtection(cart || undefined);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncProtection());
} else {
  syncProtection();
}
